import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { midiToName } from '@solfo/shared';
import { api } from '../../lib/api.ts';
import { Card, EmptyState, Spinner } from '../../lib/ui.tsx';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';

interface WarmupPlan {
  id: string;
  items: { id: string; exerciseId: string; rootMidi: number; tempoBpm: number; orderIndex: number }[];
}

/** C7 · Coach-selected warm-up sequence, runnable any time outside the plan. */
export default function Warmup() {
  const { data: plan, isLoading } = useQuery({
    queryKey: ['warmup'],
    queryFn: () => api<WarmupPlan | null>('/api/warmup'),
  });
  const { data: exercises } = useQuery({
    queryKey: ['exercises-all'],
    queryFn: () => api<{ id: string; name: string; estDurationSec: number }[]>('/api/exercises'),
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">🔥 Warm-up</h1>
      <p className="text-xs text-slate-500">
        Before choir, an audition, or a lesson — run it as often as you like. It never counts against your plan.
      </p>
      {!plan || plan.items.length === 0 ? (
        <EmptyState title="No warm-up sequence yet" hint="Your coach can set one up in a minute — send them a nudge." />
      ) : (
        <div className="space-y-2">
          {plan.items.map((item, i) => {
            const ex = exercises?.find((e) => e.id === item.exerciseId);
            const params = new URLSearchParams({
              exerciseId: item.exerciseId,
              rootMidi: String(item.rootMidi),
              tempoBpm: String(item.tempoBpm),
              mode: 'warmup',
            });
            return (
              <Link key={item.id} to={`/app/player?${params}`} className="block">
                <Card>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">
                        {i + 1}. {exLabel(ex)}
                      </p>
                      <p className="text-xs text-slate-500">
                        starts at {midiToName(item.rootMidi)} · {ex ? Math.round(ex.estDurationSec / 60) : '?'} min
                      </p>
                    </div>
                    <span className="text-lg text-brand-600">▶</span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
